<template>
  <v-container fluid>
    <v-breadcrumbs>
      <v-breadcrumbs-item :to="{name: 'Accounts'}">Accounts</v-breadcrumbs-item>
      <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
      <v-breadcrumbs-item :disabled="true">{{account.name}}</v-breadcrumbs-item>
    </v-breadcrumbs>
    <v-toolbar>
      <v-toolbar-title>
        {{account.name}}
        <span v-if="account.abbr">({{account.abbr}})</span>
      </v-toolbar-title>
    </v-toolbar>
    <v-card class="my-2">
      <v-card-title>
        Account General Info
        <v-spacer />
        <edit-account :account.sync="account" />
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col>
            <span class="font-weight-medium">Logo:</span>
            <v-img
              v-if="account.logoUrl"
              width="60px"
              :src="account.logoUrl"
            />
          </v-col>
          <v-col>
            <span class="font-weight-medium">Type:</span>
            {{account.type}}
          </v-col>
          <v-col>
            <span class="font-weight-medium">Timezone:</span>
            {{account.timezone}}
          </v-col>
          <v-col>
            <span class="font-weight-medium">User Limit:</span>
            {{account.userLimit}}
          </v-col>
          <v-col>
            <span class="font-weight-medium">Student Limit:</span>
            {{account.studentLimit}}
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="my-2">
      <v-card-title>
        Administrators
        <v-spacer />
        <edit-administrator
          @updated="savedNewAdmin"
          :locations="account.locations"
          :administrator="administrator"
        />
      </v-card-title>
      <v-card-text>
        <div v-if="account.admins && account.admins.length < 1">
          <span class="font-weight-medium">No data</span>
        </div>
        <v-data-table :headers="administratorHeaders" :items="account.admins" v-else>
          <template v-slot:item.name="{ item }">{{item.firstName}} {{item.lastName}}</template>
          <template v-slot:item.locations="{ item }">
            <v-bottom-sheet>
              <template v-slot:activator="{ on, attrs }">
                <v-chip outlined v-bind="attrs" v-on="on">{{ item.locations ? item.locations.length:0}}</v-chip>
              </template>
              <v-sheet class="text-center" min-height="100px">
                <div style="overflow-x: scroll; white-space: nowrap;">
                  <span :key="l.id" v-for="l in item.locations">
                    <v-chip class="ma-5" outlined>{{l.name}}</v-chip>
                  </span>
                </div>
              </v-sheet>
            </v-bottom-sheet>
          </template>
          <template v-slot:item.action="{ item }">
            <edit-administrator :locations="account.locations" :administrator.sync="item" />
            <v-btn @click="deleteAdmin(item)" icon>
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <location-index :locations="account.locations"/>

    <v-card class="my-2">
      <v-card-title>Terminology
        <v-spacer />
        <edit-terminology @updated="savedNewTerm" :terminology="terminology" />
      </v-card-title>
      <v-card-text>
        <div v-if="account.accountTerminologies && account.accountTerminologies.length < 1">
          <span class="font-weight-medium">No data</span>
        </div>
        <v-data-table :headers="terminologyHeaders" :items="account.accountTerminologies" v-else>
            <template v-slot:item.action="{ item }">
              <edit-terminology :terminology.sync="item" />
              <v-btn @click="deleteTerminology(item)" icon>
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-card class="my-2">
      <v-card-title>Google Suite Integration</v-card-title>
      <v-card-text>
        <v-row>
          <v-col>
            <span class="font-weight-medium">Domain:</span>
            {{account.googleCompanyDomain}}
          </v-col>
          <v-col>
            <span class="font-weight-medium">Admin:</span>
            {{account.googleAdminAccount}}
          </v-col>
          <v-col cols="12">
            <span class="font-weight-medium">API Key:</span>
            <br />
            {{account.googleApiKey}}
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="my-2">
      <v-card-title>SMTP Server</v-card-title>
      <v-card-text>
        <v-simple-table>
          <tbody>
            <tr>
                <td>Host</td>
                <td>{{account.smtpHost}}</td>
            </tr>
            <tr>
                <td>Port</td>
                <td>{{account.smtpPort}}</td>
            </tr>
            <tr>
                <td>Default From Email</td>
                <td>{{account.smtpFrom}}</td>
            </tr>
            <tr>
                <td>Username</td>
                <td>{{account.smtpUserName}}</td>
            </tr>
            <tr>
                <td>Require Authentication</td>
                <td>{{account.smtpAuth}}</td>
            </tr>
            <tr>
                <td>Enable TLS</td>
                <td>{{account.smtpStarttlsEnable}}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <div class="text-caption mt-5">
      Created on {{account.createdAt |formateDate }}
      <br />
      Last updated on {{account.updatedAt |formateDate}}
    </div>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

export default {
  data() {
    return {
      account: {},
      baseUrl: process.env.VUE_APP_BASE_URL,
      administrator: {
        accountId: this.$route.params.id,
        firstName: "",
        lastName: "",
        username: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
      },
      terminology:{
        accountId: this.$route.params.id,
        name: '',
        value: ''
      },
      administratorHeaders: [
        { text: "Name", value: "name", align: "start", sortable: false },
        {
          text: "Username",
          value: "username",
          align: "start",
          sortable: false,
        },
        { text: "Email", value: "email", align: "start", sortable: false },
        { text: "Phone", value: "phone", align: "start", sortable: false },
        {
          text: "Locations",
          value: "locations",
          align: "start",
          sortable: false,
        },
        { text: "Action", value: "action", align: "start", sortable: false },
      ],
      terminologyHeaders: [
        { text: "Name", value: "name", align: "start", sortable: false },
        { text: "Value", value: "value", align: "start", sortable: false },
        { text: "Action", value: "action", align: "start", sortable: false },
      ],
    };
  },
  components: {
    EditAccount: () => import("@/components/EditAccount.vue"),
    EditAdministrator: () => import("@/components/EditAdministrator.vue"),
    LocationIndex: () => import("@/components/location/LocationIndex.vue"),
    EditTerminology: () => import("@/components/EditTerminology.vue"),
  },
  computed: {
    ...mapGetters("navigation", ["navState", "isAppLoading"]),
  },
  filters: {
    formateDate(value) {
      return moment(value).format("MMMM Do YYYY, h:mm a");
    },
  },
  methods: {
    ...mapActions("messages", ["addErrorMessage", "addSuccessMessage"]),
    initAccount() {
      this.$axios
        .get("/account/get-by-id", {
          params: { accountId: this.$route.params.id },
        })
        .then((resp) => {
          this.account = resp.data;
        })
        .catch(() => {
          this.addErrorMessage("Error loading account from server");
        });
    },
    savedNewAdmin(admin) {
      this.account.admins.push(admin);
    },
    savedNewLocation(location) {
      this.account.locations.push(location);
    },
    savedNewTerm(term){
      this.account.accountTerminologies.push(term);
    },
    deleteAdmin(admin){
      if(confirm(`Confirm delete ${admin.firstName}?`)){
        this.$axios
        .delete("/account/admin/delete", { params: {adminUserId: admin.id}})
        .then( () => {
          this.account.admins.splice(this.account.admins.findIndex(a => a.id == admin.id), 1);
        })
        .catch((error) => {
          console.log(error)
          this.addErrorMessage("Error deleting admin from server");
        });
      }
    },
    deleteTerminology(term){
      if(confirm(`Confirm delete terminology: ${term.name}?`)){
         this.$axios
        .delete("/account/terminology/delete", { params: {accountTerminologyId: term.id}})
        .then( () => {
          this.account.accountTerminologies.splice(this.account.accountTerminologies.findIndex(a => a.id == term.id), 1);
        })
        .catch((error) => {
          console.log(error)
          this.addErrorMessage("Error deleting terminology from server");
        });
      }
    }
  },
  created() {
    this.initAccount();
  },
};
</script>